import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Send } from "lucide-react";

const PROJECT_TYPES = [
  "Property Developer",
  "Architect",
  "Interior Designer",
  "Hotel / Hospitality",
  "Private Residential Buyer",
  "Other",
];

interface BathLeadFormProps {
  productName?: string;
  productSku?: string;
}

export const BathLeadForm = ({ productName, productSku }: BathLeadFormProps = {}) => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    interest_type: PROJECT_TYPES[0],
    quantity: "",
    timeline: "",
    location: "",
    message: "",
  });

  const update = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast({ title: "Missing details", description: "Name and email are required.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const sessionId =
        typeof window !== "undefined" && (window.crypto?.randomUUID?.() ?? `bath-${Date.now()}`);

      const composedMessage = [
        productName && `Product: ${productName}${productSku ? ` (${productSku})` : ""}`,
        `Project type: ${form.interest_type}`,
        form.quantity && `Estimated quantity: ${form.quantity}`,
        form.timeline && `Timeline: ${form.timeline}`,
        form.message && `\nMessage:\n${form.message}`,
      ].filter(Boolean).join("\n");

      const interestLabel = productName
        ? `Bath & Sanitary — ${productName} — ${form.interest_type}`
        : `Bath & Sanitary — ${form.interest_type}`;

      const { error } = await supabase.functions.invoke("save-lead", {
        body: {
          name: form.name,
          email: form.email,
          phone: form.phone || undefined,
          company: form.company || undefined,
          interest_type: interestLabel,
          location: form.location || undefined,
          message: composedMessage,
          session_id: String(sessionId),
        },
      });
      if (error) throw error;
      setDone(true);
      toast({ title: "Request received", description: "A project specialist will contact you within 1 business day." });
    } catch (err) {
      if (import.meta.env.DEV) console.error(err);
      toast({ title: "Submission failed", description: "Please try again or email info@aureum7trading.com directly.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="border border-neutral-200 bg-neutral-50 p-10 text-center">
        <h3 className="font-serif text-2xl font-semibold text-neutral-900">Thank you</h3>
        <p className="mt-3 text-neutral-700">
          Your project enquiry has been received. A specialist will contact you shortly at{" "}
          <span className="font-medium text-amber-600">{form.email}</span>.
        </p>
        <p className="mt-2 text-sm text-neutral-500">
          Urgent? Email{" "}
          <a className="text-amber-600 hover:underline" href="mailto:info@aureum7trading.com">
            info@aureum7trading.com
          </a>
        </p>
      </div>
    );
  }

  const inputCls = "mt-1 bg-white border-neutral-300 text-neutral-900";

  return (
    <form onSubmit={handleSubmit} className="grid gap-4 border border-neutral-200 bg-neutral-50 p-6 sm:p-8">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="bl-name" className="text-neutral-700">Name *</Label>
          <Input id="bl-name" required value={form.name} onChange={update("name")} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="bl-company" className="text-neutral-700">Company</Label>
          <Input id="bl-company" value={form.company} onChange={update("company")} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="bl-email" className="text-neutral-700">Email *</Label>
          <Input id="bl-email" type="email" required value={form.email} onChange={update("email")} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="bl-phone" className="text-neutral-700">Phone</Label>
          <Input id="bl-phone" value={form.phone} onChange={update("phone")} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="bl-type" className="text-neutral-700">Project type</Label>
          <select
            id="bl-type"
            value={form.interest_type}
            onChange={update("interest_type")}
            className="mt-1 flex h-10 w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900"
          >
            {PROJECT_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <Label htmlFor="bl-qty" className="text-neutral-700">Estimated quantity</Label>
          <Input id="bl-qty" placeholder="e.g. 40 units" value={form.quantity} onChange={update("quantity")} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="bl-timeline" className="text-neutral-700">Timeline</Label>
          <Input id="bl-timeline" placeholder="e.g. Q3 2026" value={form.timeline} onChange={update("timeline")} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="bl-loc" className="text-neutral-700">Project location</Label>
          <Input id="bl-loc" placeholder="City, Country" value={form.location} onChange={update("location")} className={inputCls} />
        </div>
      </div>
      <div>
        <Label htmlFor="bl-msg" className="text-neutral-700">Project details</Label>
        <Textarea
          id="bl-msg"
          rows={4}
          maxLength={1500}
          placeholder="Tell us about the project: number of bathrooms, finishes required, custom branding needs…"
          value={form.message}
          onChange={update("message")}
          className={inputCls}
        />
      </div>
      <Button
        type="submit"
        disabled={submitting}
        size="lg"
        className="bg-neutral-900 text-white hover:bg-neutral-800"
      >
        {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        {submitting ? "Sending…" : "Request Project Quote"}
      </Button>
      <p className="text-xs text-neutral-500">
        Submitting sends your enquiry to <span className="font-medium text-amber-600">info@aureum7trading.com</span>.
        We respond within 1 business day.
      </p>
    </form>
  );
};

export default BathLeadForm;
